/**
 * DemandCounter — V143.1.
 *
 * Tiny inline component that shows "🔥 X čakajúcich" social proof
 * counter for a coming-soon agent. Reads from a SHARED context-free
 * cache (window.__demandCache) so all 12 cards on the funnel make
 * exactly 1 fetch in total — not 12.
 *
 * The cache is 5min-stale-while-revalidate: subsequent mounts get the
 * cached value instantly and trigger a background refresh.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Flame } from "lucide-react";
import { API } from "../../config";
import { useLanguage } from "../../contexts/LanguageContext";
import { funnelT } from "../../translations-funnel";

const TTL_MS = 5 * 60 * 1000; // 5 minutes
const _cache = { at: 0, by_slug: null, inflight: null };

async function fetchDemand() {
  const now = Date.now();
  if (_cache.by_slug && now - _cache.at < TTL_MS) {
    return _cache.by_slug;
  }
  if (_cache.inflight) return _cache.inflight;
  _cache.inflight = (async () => {
    try {
      const r = await axios.get(`${API}/agent-waitlist/demand`);
      const by_slug = {};
      for (const row of r.data.rows || []) {
        by_slug[row.agent_slug] = row.signups || 0;
      }
      _cache.by_slug = by_slug;
      _cache.at = now;
      return by_slug;
    } finally {
      _cache.inflight = null;
    }
  })();
  return _cache.inflight;
}

export default function DemandCounter({ agentSlug }) {
  const [count, setCount] = useState(null);
  const { language } = useLanguage();
  const ft = funnelT(language);

  useEffect(() => {
    let cancelled = false;
    fetchDemand()
      .then((by_slug) => {
        if (cancelled) return;
        setCount(by_slug[agentSlug] || 0);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [agentSlug]);

  if (count === null) return null;
  if (count === 0) return null;

  // Slavic-style pluralization rules (1 / 2-4 / 5+) work for SK/CS/PL/HU/UK;
  // EN/DE use a simpler "1 / many" split — same dict structure either way.
  const label = count === 1
    ? ft.dc_waiting_1
    : count < 5
      ? ft.dc_waiting_few
      : ft.dc_waiting_many;

  return (
    <span
      data-testid={`demand-counter-${agentSlug}`}
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-black text-amber-300 bg-amber-500/10 border border-amber-500/30 px-2 py-1"
    >
      <Flame className="w-3 h-3" />
      {count} {label}
    </span>
  );
}
